import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import profileImage from "../../Image/Profile.png";
import { useSelector } from "react-redux";

const Navbar1 = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((state) => state.profile);

  // Handle mobile menu closing on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle navbar appearance on scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Check if a nav item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white shadow-lg py-2"
          : "bg-white/95 backdrop-blur-sm py-4"
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div
            className="flex items-center space-x-2 cursor-pointer group"
            onClick={() => navigate("/")}
          >
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-300">
              {profile?.p_name || "Ads Manager"}
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavButton
              label="Dashboard"
              onClick={() => navigate("/")}
              isActive={isActive("/")}
            />
            <NavButton
              label="Ads"
              onClick={() => navigate("/Ads/add")}
              isActive={isActive("/Ads/add")}
            />
          </div>

          {/* Desktop Profile */}
          <div className="hidden md:flex items-center">
            <div
              className="flex items-center space-x-3 bg-gray-100 hover:bg-gray-200 rounded-full px-4 py-2 cursor-pointer transition-all duration-200"
              onClick={() => navigate("/all/profile")}
            >
              <span className="text-sm font-medium text-gray-700">
                {profile?.p_name ? profile.p_name.split(" ")[0] : "Profile"}
              </span>
              <div className="relative">
                <img
                  src={!profile.p_icon ? profileImage : profile?.p_icon}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-white"
                />
                <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? "max-h-60" : "max-h-0"
        }`}
      >
        <div className="bg-white px-4 pt-2 pb-3 shadow-inner border-t border-gray-100">
          <MobileNavButton
            label="Dashboard"
            onClick={() => navigate("/")}
            isActive={isActive("/")}
          />
          <MobileNavButton
            label="Ads"
            onClick={() => navigate("/Ads/add")}
            isActive={isActive("/Ads/add")}
          />
          <div
            className="flex items-center space-x-3 px-3 py-3 rounded-lg mb-2 cursor-pointer hover:bg-gray-100"
            onClick={() => navigate("/all/profile")}
          >
            <img
              src={!profile.p_icon ? profileImage : profile?.p_icon}
              alt="Profile"
              className="w-8 h-8 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-medium">My Profile</p>
              <p className="text-xs text-gray-500">
                {profile?.p_name || "Account settings"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Desktop navigation button component
const NavButton = ({ label, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`relative px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
      isActive
        ? "text-blue-600"
        : "text-gray-700 hover:text-blue-600 hover:bg-gray-100"
    }`}
  >
    {label}
    {isActive && (
      <span className="absolute bottom-0 left-0 right-0 mx-auto w-12 h-0.5 bg-blue-600 rounded-full" />
    )}
  </button>
);

// Mobile navigation button component
const MobileNavButton = ({ label, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`block w-full text-left px-3 py-3 rounded-lg text-base font-medium mb-1 ${
      isActive ? "bg-blue-50 text-blue-600" : "hover:bg-gray-100 text-gray-700"
    }`}
  >
    <div className="flex items-center">
      {label === "Dashboard" ? (
        <svg
          className="w-5 h-5 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ) : (
        <svg
          className="w-5 h-5 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      )}
      {label}
    </div>
  </button>
);

export default Navbar1;
